import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";

import AppDialog from "components/AppDialog";
import FormLabel, { FORM_INPUT_CLASSNAME } from "components/base/FormLabel";
import ActionButton from "components/UserCardList/components/UserCardResult/molecules/ActionButton";
import { useContext } from "react";
import { JobsContext, MatchContext } from "context/providers";
import { JOB_APPLICATION_MATCH_STATUS } from "lookup";

const READ_ONLY_HINT_CLASSNAME = "font-inter text-xs font-normal text-gray-500";

const MatchModal = ({
  onClose,
  applicationId,
  jobOpportunityId,
  userId,
  userIsUnclaimed,
  isJobLongList,
  freelancerPitchPrefill,
  freelancerPitch,
  isUpdate,
  status,
  resumeLocation,
  hitStatus,
}) => {
  const [objectInputField, setObjectInputField] = useState({
    value: freelancerPitchPrefill || "",
    successMessage: "",
    errorMessage: "",
  });

  const [isLoading, setIsLoading] = useState(false);
  const { jobOpp, convertJobLongListToMatch } = useContext(JobsContext);
  const { createMatch, updateMatch } = useContext(MatchContext);

  const handleOnChange = (e) => {
    setObjectInputField((prev) => ({
      ...prev,
      value: e.target.value,
      errorMessage: "",
      successMessage: "",
    }));
  };

  const handleSubmit = async () => {
    setIsLoading(true);
    let successMessage = "";
    let errorMessage = "";

    try {
      const params = {};
      if (status) {
        params.status = status;
      }

      if (isUpdate) {
        if (freelancerPitchPrefill !== objectInputField.value) {
          params.freelancerPitchPrefill = objectInputField.value;
          await updateMatch(applicationId, jobOpportunityId, params);
          successMessage = "update successful";

          status && onClose();
        }
      } else {
        if (objectInputField.value) {
          params.freelancerPitchPrefill = objectInputField.value;
        }

        if (userIsUnclaimed) {
          params.unclaimedWhenMatched = true;
        }

        if (isJobLongList && resumeLocation) {
          params.resumeLocation = resumeLocation;
        }

        await createMatch(jobOpportunityId, userId, params);
        if (isJobLongList) {
          await convertJobLongListToMatch(jobOpportunityId, userId);
        }
        onClose();
      }
    } catch (error) {
      console.log("matchAction error: ", error);
      errorMessage = "Something went wrong";
    }
    setObjectInputField((prev) => ({
      ...prev,
      successMessage,
      errorMessage,
    }));
    setIsLoading(false);
  };

  const readOnlyInput = (title, text) => {
    const fieldId = `match-${title.toLowerCase().replace(/\s+/g, "-")}`;

    return (
      <div className="flex w-full flex-col">
        <FormLabel size="base" htmlFor={fieldId}>
          {title} <span className={READ_ONLY_HINT_CLASSNAME}>(Read Only)</span>
        </FormLabel>
        <textarea
          id={fieldId}
          readOnly
          value={text || ""}
          rows={6}
          className={`mt-1 max-h-[175px] w-full cursor-default overflow-auto whitespace-pre-line p-2 outline-none transition-all ${FORM_INPUT_CLASSNAME}`}
        />
      </div>
    );
  };

  const qualifiedStatuses = useMemo(
    () => [
      JOB_APPLICATION_MATCH_STATUS.MATCHED,
      JOB_APPLICATION_MATCH_STATUS.INTERESTED,
      JOB_APPLICATION_MATCH_STATUS.SHORTLISTED,
      JOB_APPLICATION_MATCH_STATUS.PRESHORTLISTED,
      JOB_APPLICATION_MATCH_STATUS.TKSHORTLISTED,
    ],
    []
  );

  const isNotQualifiedStatus = useMemo(
    () => !qualifiedStatuses.includes(hitStatus),
    [hitStatus, qualifiedStatuses]
  );

  return (
    <AppDialog
      open={!!((applicationId || userId) && jobOpportunityId)}
      onClose={onClose}
      header="Application Questions"
      className="max-h-[90vh] w-[95%] max-w-none overflow-x-hidden rounded-lg md:w-10/12 xl:w-7/12"
      contentClassName="px-6 pb-6 pt-4"
      testId="match-modal"
    >
      <>
        {hitStatus !== JOB_APPLICATION_MATCH_STATUS.MATCHED &&
        hitStatus !== JOB_APPLICATION_MATCH_STATUS.INTERESTED &&
        freelancerPitch ? (
          readOnlyInput("Answered Questions", freelancerPitch)
        ) : (
          <>
            {jobOpp.freelancerPitchPrefill &&
              readOnlyInput("Job Questions", jobOpp.freelancerPitchPrefill)}
            <div className="flex flex-col gap-y-4">
              {isNotQualifiedStatus && isUpdate ? (
                readOnlyInput("Candidate Questions", objectInputField.value)
              ) : (
                <div className="flex w-full flex-col">
                  <FormLabel size="base" htmlFor="candidate-questions">
                    Candidate Questions
                  </FormLabel>
                  <textarea
                    id="candidate-questions"
                    value={objectInputField.value}
                    onChange={handleOnChange}
                    rows={6}
                    className={`mt-1 w-full p-2 outline-none transition-all ${FORM_INPUT_CLASSNAME}`}
                  />
                </div>
              )}
              <div className="relative">
                <div className="mt-6 flex justify-between">
                  <div className="flex flex-col">
                    {objectInputField.successMessage && (
                      <span className="mt-1 text-sm font-bold text-green-600">
                        {objectInputField.successMessage}
                      </span>
                    )}
                    {objectInputField.errorMessage && (
                      <span className="mt-1 text-sm font-bold text-red-600">
                        {objectInputField.errorMessage}
                      </span>
                    )}
                  </div>

                  <div>
                    <ActionButton
                      testId="match-modal-submit-button"
                      isDisabled={
                        isLoading ||
                        freelancerPitchPrefill === objectInputField.value
                      }
                      isLoading={isLoading}
                      action={() => handleSubmit()}
                      className="border-b-2 text-gray-600"
                      title={
                        isUpdate && !status ? "Update Match" : "Create Match"
                      }
                      isUpdate={isUpdate && !status}
                      type="match"
                      iconType="heart"
                    />
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </>
    </AppDialog>
  );
};

MatchModal.propTypes = {
  onClose: PropTypes.func.isRequired,
};

export default MatchModal;
